import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { marked } from "marked";
import { toast } from "react-toastify";
import { wait } from "../utils/HelperFunction/textFormatting";
import CaseSearchDialogHeader from "./UI/CaseSearchDialogHeader";
import { printContent } from "../utils/HelperFunction/actions";
import SearchInputField from "./UI/SearchInputField";

const markdownStyles = {
  h2: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "15px",
    color: "#1e3a8a",
    fontWeight: "600",
    marginTop: "1.5em",
    marginBottom: "1em",
  },
  h3: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: "bold",
    marginBottom: "4px",
    fontSize: "14px",
  },
  h4: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: "bold",
    marginTop: "6px",
    marginBottom: "6px",
    fontSize: "13px",
  },
  p: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "13px",
    color: "#334155",
    marginBottom: "1.25em",
    lineHeight: "1.5",
  },
  ol: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "14px",
    marginBottom: "6px",
    paddingLeft: "20px",
    listStyleType: "decimal",
  },
  ul: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "14px",
    marginBottom: "6px",
    paddingLeft: "20px",
    listStyleType: "disc",
  },
  li: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "13px",
    color: "#334155",
    lineHeight: "1.5",
  },
  strong: {
    fontWeight: 600,
  },
  mark: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    backgroundColor: "#ffeb3b",
    padding: "0 2px",
    fontSize: "inherit",
    fontWeight: "inherit",
  },
};

const CaseSummary = ({ closeOnBack, title, summary }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [totalMatches, setTotalMatches] = React.useState(0);
  const [currentMatch, setCurrentMatch] = React.useState(0);
  const [isCopied, setIsCopied] = useState(false);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentMatch(0);
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlightedContent = useMemo(() => {
    if (!searchTerm) {
      setTotalMatches(0);
      return summary;
    }

    const keywordPattern = new RegExp(`(${escapeRegExp(searchTerm)})`, "gi");
    let matchIndex = 0;

    const contentWithHighlights = summary.split(keywordPattern);

    const highlighted = contentWithHighlights
      .map((part) => {
        // Check for exact match to avoid regex.test issues
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
          const isCurrent = matchIndex === currentMatch;
          const style = isCurrent ? 'style="border: 1px solid #303f9f;"' : "";
          const marked = `<mark class="highlight" data-index="${matchIndex}" ${style}>${part}</mark>`;
          matchIndex++;
          return marked;
        }
        return part;
      })
      .join("");

    setTotalMatches(matchIndex);
    return highlighted;
  }, [summary, searchTerm, currentMatch]);

  const generateCSS = (styles) => {
    let cssString = "";
    for (const tag in styles) {
      cssString += `.markdown ${tag} {\n`;
      for (const property in styles[tag]) {
        const cssProperty = property.replace(/([A-Z])/g, "-$1").toLowerCase();
        cssString += `  ${cssProperty}: ${styles[tag][property]};\n`;
      }
      cssString += "}\n";
    }
    return cssString;
  };

  const handlePrint = () => {
    const markdownContent = summary;
    const htmlContent = marked(markdownContent);
    const cssString = generateCSS(markdownStyles);
    printContent({
      content: htmlContent,
      cssString,
      disclaimer:
        "LawGPT provides relevant information but is not a replacement for legal advice.",
    });
  };

  const handleCopy = async () => {
    setIsCopied(true);
    try {
      const markdownContent = summary || "";
      await navigator.clipboard.writeText(markdownContent);
      await wait();
      setIsCopied(false);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        const markdownContent = summary;
        await navigator.share({ title: "Case Summary", text: markdownContent });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      toast.error("Share is not supported in your browser.", {
        position: "top-right",
      });
    }
  };

  useEffect(() => {
    const match = document.querySelector(
      `.highlight[data-index="${currentMatch}"]`
    );
    if (match) {
      match.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentMatch]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100%",
        boxSizing: "border-box",
        boxShadow: {
          xs: "none",
          sm: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
        background: "#ffffff",
        overflowY: "auto",
        "::-webkit-scrollbar": {
          width: "5px",
        },
        "::-webkit-scrollbar-track": {
          background: "#fff",
        },
        "::-webkit-scrollbar-thumb": {
          background: "#bbb",
          borderRadius: "6px",
        },
      }}
    >
      <React.Fragment>
        {!highlightedContent ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              boxSizing: "border-box",
              minHeight: "150px",
              bgcolor: "#fff",
              p: "16px",
              m: 1,
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              overflowY: "auto",
              "::-webkit-scrollbar": {
                width: "5px",
              },
              "::-webkit-scrollbar-track": {
                background: "#fff",
              },
              "::-webkit-scrollbar-thumb": {
                background: "#bbb",
                borderRadius: "6px",
              },
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: { xs: "20px", lg: "30px" },
                mb: 1,
                color: "#ff9800",
              }}
            />
            <Typography
              sx={{
                textAlign: "center",
                fontSize: { xs: "12px", lg: "14px" },
                color: "#555",
                fontWeight: "bold",
              }}
            >
              Case summary not found
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <CaseSearchDialogHeader
              component="summary"
              onClose={closeOnBack}
              isCopied={isCopied}
              onCopy={handleCopy}
              onShare={handleShare}
              onPrint={handlePrint}
            />
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                boxSizing: "border-box",
                background: "#fff",
                mt: "8px",
              }}
            >
              <SearchInputField
                searchTerm={searchTerm}
                onSearch={handleSearchChange}
                currentMatch={currentMatch}
                setCurrentMatch={setCurrentMatch}
                totalMatches={totalMatches}
              />
            </Box>
            {/* Summary Content */}
            <Box
              sx={{
                display: "flex",
                boxSizing: "border-box",
                flexDirection: "column",
                height: "100%",
                overflowY: "auto",
                bgcolor: "#ffffff",
                p: "8px 12px",
                m: 1,
                borderRadius: "8px",
                "::-webkit-scrollbar": {
                  width: "5px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#fff",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              {/* Case Title */}
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 600,
                  color: "#1e3a8a",
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  //mb: 1.5,
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
              <Typography sx={{ textAlign: "justify" }}>
                <Markdown
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({ node, ...props }) => (
                      <Typography sx={{ display: "none" }} {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <Typography sx={markdownStyles.h2} {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <Typography sx={markdownStyles.h3} {...props} />
                    ),
                    h4: ({ node, ...props }) => (
                      <Typography sx={markdownStyles.h4} {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <Typography sx={markdownStyles.p} {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <Box component="ol" sx={markdownStyles.ol} {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <Box component="ul" sx={markdownStyles.ul} {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <Typography
                        component="li"
                        sx={markdownStyles.li}
                        {...props}
                      />
                    ),
                    // strong: ({ node, ...props }) => <BulletStrong {...props} />,
                    mark: ({ node, ...props }) => (
                      <Typography
                        component="mark"
                        sx={markdownStyles.mark}
                        {...props}
                      />
                    ),
                  }}
                >
                  {highlightedContent}
                </Markdown>
              </Typography>
            </Box>
          </>
        )}
      </React.Fragment>
    </Box>
  );
};

export default CaseSummary;
